import { EVENTS } from 'util.events'

// Inline header suggestions must not lock the storefront behind them.
const scrollLockOwners = new Set()
// URL keys include the locale-aware storefront root and search options.
const suggestionCache = new Map()
const suggestionCacheLimit = 15
const suggestionCacheLifetime = 60000

class PredictiveSearch extends HTMLElement {
  constructor() {
    super()
    this.searchRequest = 0
    this.enabled = this.getAttribute('data-enabled')
    this.context = this.getAttribute('data-context')
    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector('#predictive-search')
    this.closeBtn = this.querySelector('.element-button--close-search')
    this.screen = this.querySelector('[data-screen]')
    this.SearchModal = this.closest('#SearchModal') || null

  }

  connectedCallback() {
    if (this.enabled === 'false') return

    this.abortController?.abort()
    this.abortController = new AbortController()
    this.resultsSizeObserver?.disconnect()
    if (typeof ResizeObserver !== 'undefined' && this.context === 'header') {
      this.resultsSizeObserver = new ResizeObserver(entries => {
        const size = entries[0]?.borderBoxSize
        const height = (Array.isArray(size) ? size[0]?.blockSize : size?.blockSize) || 0
        this.querySelector('form')?.style.setProperty('--anu-search-results-height', `${height}px`)
      })
      this.resultsSizeObserver.observe(this.predictiveSearchResults)
    }
    this.modalObserver?.disconnect()
    if (this.SearchModal) {
      this.modalObserver = new MutationObserver(() => {
        if (this.SearchModal.classList.contains('modal--is-active')) this.focusInputSoon()
      })
      this.modalObserver.observe(this.SearchModal, { attributes: true, attributeFilter: ['class'] })
    }

    // Open events
    document.addEventListener(
      EVENTS.predictiveSearchOpen,
      (e) => {
        if (e.detail.context !== this.context) return
        this.classList.add('is-active')

        // Wait for opening events to finish then apply focus
        this.focusInputSoon()

        if (this.context !== 'header' || this.SearchModal) {
          scrollLockOwners.add(this)
          document.body.classList.add('predictive-overflow-hidden')
        }
      },
      { signal: this.abortController.signal }
    )

    // Give immediate feedback, then request suggestions after a short pause.
    this.input.addEventListener('keydown', () => {
      this.classList.add('is-active')
    }, { signal: this.abortController.signal })
    this.input.addEventListener('input', () => {
      clearTimeout(this.inputTimer)
      this.cancelSearchRequest()
      if (!this.input.value.trim()) { this.close(); return }
      this.inputTimer = setTimeout(() => {
        this.inputTimer = null
        if (this.isConnected) this.onChange()
      }, 150)
    }, { signal: this.abortController.signal })

    document.addEventListener('click', (event) => {
      if (this.classList.contains('is-active') && !this.contains(event.target)) this.close()
    }, { signal: this.abortController.signal })

    document.addEventListener('focusin', (event) => {
      if (this.classList.contains('is-active') && !this.contains(event.target)) this.close()
    }, { signal: this.abortController.signal })

    // Close events
    document.addEventListener(
      EVENTS.predictiveSearchClose,
      () => {
        this.close()
      },
      { signal: this.abortController.signal }
    )

    document.addEventListener(
      'keydown',
      (evt) => {
        if (evt.keyCode === 27) this.close()
      },
      { signal: this.abortController.signal }
    )

    this.closeBtn?.addEventListener(
      'click',
      (evt) => {
        evt.preventDefault()
        this.close()
      },
      { signal: this.abortController.signal }
    )

    this.screen?.addEventListener(
      'click',
      () => {
        this.close()
      },
      { signal: this.abortController.signal }
    )

    document.addEventListener(EVENTS.headerSearchClose, () => {
      this.close()
    }, { signal: this.abortController.signal })
  }

  disconnectedCallback() {
    this.abortController?.abort()
    this.resultsSizeObserver?.disconnect()
    this.modalObserver?.disconnect()
    clearTimeout(this.inputTimer)
    clearTimeout(this.focusTimer)
    this.cancelSearchRequest()
    if (scrollLockOwners.delete(this) && !scrollLockOwners.size) {
      document.body.classList.remove('predictive-overflow-hidden')
    }
  }

  onChange() {
    const searchTerm = this.input.value.trim()
    if (!searchTerm.length) { this.close(); return }

    this.classList.add('is-active')
    this.getSearchResults(searchTerm)
  }

  cancelSearchRequest() {
    this.searchRequest++
    this.requestController?.abort()
    this.requestController = null
  }

  focusInputSoon() {
    clearTimeout(this.focusTimer)
    this.focusTimer = setTimeout(() => {
      if (this.isConnected) this.input.focus()
    }, 100)
  }

  async getSearchResults(searchTerm) {
    this.cancelSearchRequest()
    const requestId = this.searchRequest
    const searchObj = {
      q: searchTerm,
      'resources[limit]': 3,
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    }
    const params = this.paramUrl(searchObj)
    const url = `${window.Shopify.routes.root}search/suggest?${params}&section_id=search-results`
    const cached = suggestionCache.get(url)
    if (cached && Date.now() - cached.savedAt < suggestionCacheLifetime) {
      // Refresh insertion order so the least recently used entry is evicted.
      suggestionCache.delete(url)
      suggestionCache.set(url, cached)
      if (this.isConnected && this.classList.contains('is-active')) {
        this.predictiveSearchResults.innerHTML = cached.markup
        this.open()
      }
      return
    }
    suggestionCache.delete(url)
    const controller = new AbortController()
    this.requestController = controller
    try {
      const response = await fetch(url, { signal: controller.signal })
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`)
      const text = await response.text()
      if (requestId !== this.searchRequest || controller.signal.aborted || !this.isConnected || !this.classList.contains('is-active')) return
      const results = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-search-results')
      if (!results) throw new Error('Search results markup missing')
      const markup = results.innerHTML
      suggestionCache.set(url, { markup, savedAt: Date.now() })
      while (suggestionCache.size > suggestionCacheLimit) suggestionCache.delete(suggestionCache.keys().next().value)
      this.predictiveSearchResults.innerHTML = markup
      this.open()
    } catch (error) {
      // Aborted or superseded queries must not close the latest results.
      if (error.name === 'AbortError' || controller.signal.aborted || requestId !== this.searchRequest) return
      this.close()
    } finally {
      if (this.requestController === controller) this.requestController = null
    }
  }

  open() {
    this.predictiveSearchResults.style.display = 'block'
  }

  close() {
    clearTimeout(this.inputTimer)
    clearTimeout(this.focusTimer)
    this.cancelSearchRequest()
    this.predictiveSearchResults.style.display = 'none'
    this.predictiveSearchResults.innerHTML = ''
    this.classList.remove('is-active')
    if (scrollLockOwners.delete(this) && !scrollLockOwners.size) {
      document.body.classList.remove('predictive-overflow-hidden')
    }
    if (this.contains(document.activeElement)) document.activeElement.blur()

    /**
     * @event predictive-search:close-all
     * @description Fired when the predictive search is closed.
     */
    this.dispatchEvent(new CustomEvent(EVENTS.predictiveSearchCloseAll, { bubbles: true }))
  }

  paramUrl(obj) {
    return Object.keys(obj)
      .map(function (key) {
        return key + '=' + encodeURIComponent(obj[key])
      })
      .join('&')
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch)



